import {
  type QueryClient,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type { InternalNumberField } from 'v2/pages/settings/forwarding-contacts/internal-number-sheet'
import { settingsService } from 'v2/shared/services/settings-service'
import { getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import type { ConvChannels } from 'v2/types/api/conversations'
import type { RPhoneNumbersPaginated } from 'v2/types/api/settings'
import { complianceLinkMutationKey } from '../number-compliance-v2/queries-v2'
import type { ConfigureNumberForm } from './configure-number.config'

// Account Numbers
// Add queryparams or types as needed
export const accountNumbersQueryParamsMap = {
  simple: (channel: ConvChannels) => ({
    fields: 'number,id,name_tag',
    paginated: false,
    sort_by: 'number',
    external_callers: false,
    channel,
    include_sender_ids: true,
  }),
  channelSpecific: (channel: ConvChannels) => ({
    channel,
  }),
  senderId: () => ({ only_sender_id: true }),
  allChannels: () => ({}),
  externalCallerId: () => ({
    only_external: true,
  }),
  tollfreeUnverified: () => ({
    type: 'tollfree',
    tendlc_registration_status: 'unregistered',
    toll_free_sms_verification: 'unverified',
  }),
}

// An important query that fetches account numbers
// Used in multiple places - PhoneNumbers, WhatsappNumbers, SenderIDs
export const useAccountNumbersQuery = (
  type: keyof typeof accountNumbersQueryParamsMap,
  channel?: ConvChannels,
  // Temporary flag to control query execution when coming from channel page
  enabled = true,
) => {
  const paramsFn = accountNumbersQueryParamsMap[type]
  const params = channel
    ? paramsFn(channel)
    : paramsFn.length === 0
      ? (paramsFn as () => Record<string, unknown>)()
      : accountNumbersQueryParamsMap.allChannels()
  return useQuery({
    queryKey: getSettingsQueryKeys().accountNumbers(params),
    queryFn: async () => {
      const response = await settingsService.getAccountNumbers(params)
      return response.data
    },
    enabled,
  })
}

// The KYC state carries an individual account's number count, which its cap is checked against.
const invalidateRentedNumbers = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().accountNumbersBase })
  queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().indiaKycBase })
}

export const useBuyNumberMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: getSettingsQueryKeys().buyNumberMutation,
    mutationFn: ({
      number,
      complianceApplicationId,
      haEnabled,
      subAccount,
      rerent,
    }: {
      number: string
      complianceApplicationId?: string
      haEnabled?: boolean
      subAccount?: string
      rerent?: boolean
    }) => settingsService.buyNumber(number, complianceApplicationId, haEnabled, subAccount, rerent),
    onSettled: () => {
      invalidateRentedNumbers(queryClient)
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().availableNumbersBase })
    },
  })
}

export const useDeleteNumberMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (number: string) => settingsService.deleteNumber(number.split('+')[1]),
    onSuccess: () => invalidateRentedNumbers(queryClient),
  })
}

// Numbers are addressed digit-only everywhere below — as a mutation key, a path segment, or a
// request field. Route every such use through this so the forms cannot disagree.
const digitsOnly = (number = '') => number.replace(/^\+/, '')

// Bulk unrent (release) selected numbers in a single request. The core endpoint loops the
// numbers server-side (mirroring the legacy console) and returns a per-number failure list.
// Numbers are sent digit-only (no leading '+'), matching the single-delete path.
export const useBulkUnrentNumbersMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (numbers: string[]) => {
      const response = await settingsService.bulkDeleteNumbers({
        numbers: numbers.map(digitsOnly),
      })
      return response.data
    },
    onSuccess: () => invalidateRentedNumbers(queryClient),
  })
}

export const useDeregisterWhatsappNumberMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (phoneNumberId: string) => settingsService.deregisterWhatsappNumber(phoneNumberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().accountNumbersBase })
    },
  })
}

export const useAddInternalNumberMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: InternalNumberField) => {
      const response = await settingsService.addInternalNumber(payload)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().internalNumbers })
    },
  })
}
// Query to update the number details
// Used in PhoneNumbers, WhatsappNumbers, ExternalCallerIds
export const useConfigureNumberMutation = (number: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: getSettingsQueryKeys().configureNumberMutation(digitsOnly(number)),
    mutationFn: async (payload: ConfigureNumberForm) => {
      const response = await settingsService.updatePhoneNumberConfiguration(number, payload)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().accountNumbersBase })
    },
  })
}

// Sheet footers render outside the configure form, so they cannot read the mutation's own
// isPending and have to ask the mutation cache. useIsMutating counts only pending mutations —
// never enumerate the cache and index it, which reads a settled save instead and silently
// drops the button loader (see pages/AGENTS.md).
export const useIsConfigureNumberPending = (number?: string) => {
  const saving = useIsMutating({
    mutationKey: getSettingsQueryKeys().configureNumberMutation(digitsOnly(number)),
  })
  // The save links the KYC before or after the PATCH, so the link counts too.
  return saving + useIsMutating({ mutationKey: complianceLinkMutationKey }) > 0
}

export const useIsBuyNumberPending = () =>
  useIsMutating({ mutationKey: getSettingsQueryKeys().buyNumberMutation }) > 0

// Per-number CNAM Lookup toggle (US-only). Accepts a list so a single edit sends one number
// and the bulk action sends many. Invalidates the numbers list so the table/sidebar refetch.
export const useUpdateNumberCnamLookupMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: getSettingsQueryKeys().updateNumberCnamLookupMutation,
    mutationFn: async (payload: { numbers: string[]; cnam_lookup: boolean }) => {
      const response = await settingsService.updateNumberCnamLookup(payload)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().accountNumbersBase })
    },
  })
}

// Per-number CNAM (Caller-ID NAME) string (US-only). Accepts a list (single or bulk).
export const useUpdateNumberCnamMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: getSettingsQueryKeys().updateNumberCnamMutation,
    mutationFn: async (payload: { numbers: string[]; cnam: string }) => {
      const response = await settingsService.updateNumberCnam(payload)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().accountNumbersBase })
    },
  })
}

export const useDeleteInternalNumberMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (contact_id: number) => {
      const response = await settingsService.deleteInternalNumber(contact_id)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().internalNumbers })
    },
  })
}

export const useUpdateInternalNumberMutation = (contact_id: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: InternalNumberField) => {
      const response = await settingsService.updateInternalNumber(contact_id, payload)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().internalNumbers })
    },
  })
}
// New paginated phone numbers query
export const phoneNumbersQueryConfig = () => ({
  queryKey: getSettingsQueryKeys().phoneNumbers,
  fetchService: (queryParams: Record<string, unknown>) => {
    if (typeof queryParams.pattern === 'string') {
      queryParams.pattern = queryParams.pattern.replace(/[\s\-+().,;/%@!=&]/g, '')
    }
    return settingsService.getPhoneNumbers(queryParams)
  },
  extractData: (data: RPhoneNumbersPaginated) => ({
    data: data.numbers,
    totalCount: data.total,
  }),
})

// Phone number select query config - always includes waba_linked field
export const phoneNumbersSelectQueryConfig = () => ({
  queryKey: getSettingsQueryKeys().phoneNumbersSelect,
  fetchService: (queryParams: Record<string, unknown>) =>
    settingsService.getPhoneNumbers({ ...queryParams, include_waba_linked: true }),
  extractData: (data: RPhoneNumbersPaginated) => ({
    data: data.numbers,
    totalCount: data.total,
  }),
})

/**
 * `enabled` defaults to true so existing callers are unchanged. It exists for
 * callers that mount unconditionally but may not be permitted to read numbers -
 * gating the render alone still fires the request, which then 403s.
 */
export const usePhoneNumbersQuery = (params: Record<string, unknown>, enabled = true) =>
  useQuery({
    queryKey: [...getSettingsQueryKeys().phoneNumbers, params],
    queryFn: () => phoneNumbersQueryConfig().fetchService(params),
    enabled,
  })

// Buyable countries per the account's number profile (SER-5858).
export const useAllowedBuyCountriesQuery = () =>
  useQuery({
    queryKey: getSettingsQueryKeys().allowedBuyCountriesBase,
    queryFn: async () => {
      const response = await settingsService.getAllowedBuyCountries()
      return response.data
    },
  })
